import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';
import Mascot from '../../components/ui/Mascot';
import ScratchEditor from '../../components/editor/ScratchEditor';
import {api, getToken} from '../../lib/api';
import {sampleLesson} from '../../data/mock';
import styles from './Lab.module.scss';

export default function Lab () {
    const {questId} = useParams();
    const lessonId = questId ?? sampleLesson.id;
    const [lesson, setLesson] = useState(sampleLesson);
    const [done, setDone] = useState([]);
    const [guideOpen, setGuideOpen] = useState(() => (
        typeof window === 'undefined' || !window.matchMedia?.('(max-width: 1380px)').matches
    ));
    const [activeStep, setActiveStep] = useState(sampleLesson.steps[0]);
    const [source, setSource] = useState('loading');
    const [saveRequestId, setSaveRequestId] = useState(0);
    const [evaluateRequestId, setEvaluateRequestId] = useState(0);
    const [savedProjectId, setSavedProjectId] = useState(null);
    const [sourceLessonId, setSourceLessonId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [checking, setChecking] = useState(false);
    const [evaluation, setEvaluation] = useState(null);
    const [saveStatus, setSaveStatus] = useState('');

    useEffect(() => {
        let alive = true;
        setSavedProjectId(null);
        setSourceLessonId(null);
        api.getLesson(lessonId).then(({lesson: remoteLesson}) => {
            if (!alive) return;
            setSourceLessonId(remoteLesson.id || lessonId);
            if (getToken()) {
                api.getLessonProject(lessonId).then(({project}) => {
                    if (!alive) return;
                    if (!project) {
                        setLesson(remoteLesson);
                        setSource('backend');
                        return;
                    }
                    setSavedProjectId(project.id);
                    localStorage.setItem(`cq_lesson_project_${lessonId}`, project.id);
                    setLesson({
                        ...remoteLesson,
                        title: project.title || remoteLesson.title,
                        starter: {
                            ...remoteLesson.starter,
                            projectUrl: `/api/projects/${project.id}/file`
                        }
                    });
                    setSource('saved-project');
                }).catch(() => {
                    if (!alive) return;
                    setLesson(remoteLesson);
                    setSource('backend');
                });
            } else {
                setLesson(remoteLesson);
                setSource('backend');
            }
            setActiveStep(remoteLesson.steps[0] ?? null);
        }).catch(() => {
            if (!alive) return;
            api.getProject(lessonId).then(({project}) => {
                if (!alive) return;
                setSavedProjectId(project.id || lessonId);
                setLesson({
                    ...sampleLesson,
                    id: lessonId,
                    title: project.title,
                    intro: project.description || '继续编辑你保存过的 Scratch 作品。',
                    starter: {
                        ...sampleLesson.starter,
                        projectUrl: `/api/projects/${lessonId}/file`
                    }
                });
                setActiveStep(sampleLesson.steps[0]);
                setSource('saved-project');
            }).catch(() => {
                if (!alive) return;
                setLesson(sampleLesson);
                setActiveStep(sampleLesson.steps[0]);
                setSource('offline');
            });
        });
        return () => {
            alive = false;
        };
    }, [lessonId]);

    useEffect(() => {
        if (!getToken()) return;
        let alive = true;
        api.getProgress().then(({progress}) => {
            if (!alive) return;
            const saved = progress.find(p => p.quest_id === lessonId || p.questId === lessonId);
            if (!saved || !lesson.steps.length) return;
            const completed = Math.round((saved.percent / 100) * lesson.steps.length);
            setDone(lesson.steps.slice(0, completed).map(step => step.n));
        }).catch(() => {});
        return () => {
            alive = false;
        };
    }, [lessonId, lesson.steps]);

    const saveProgress = (nextDone, totalSteps) => {
        if (!getToken()) {
            setSaveStatus('当前进度只保存在本页，登录后可同步保存');
            return;
        }
        const percent = Math.round((nextDone.length / totalSteps) * 100);
        api.saveProgress({
            questId: lessonId,
            status: percent >= 100 ? 'done' : 'current',
            percent
        }).then(() => {
            setSaveStatus(percent >= 100 ? '进度已完成并同步到任务地图' : '进度已保存');
        }).catch(err => {
            setSaveStatus(err.message || '进度保存失败');
        });
    };

    const toggle = n => {
        setDone(prev => {
            const nextDone = prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n].sort((a, b) => a - b);
            saveProgress(nextDone, lesson.steps.length);
            return nextDone;
        });
    };

    const progress = lesson.steps.length ? Math.round((done.length / lesson.steps.length) * 100) : 0;

    const requestSave = () => {
        if (!getToken()) {
            setSaveStatus('请先登录，再保存你的录音、角色和积木作品');
            return;
        }
        if (saving) return;
        setSaving(true);
        setSaveStatus('正在从 Scratch 编辑器打包作品…');
        setSaveRequestId(Date.now());
    };

    const handleProjectData = async blob => {
        try {
            setSaveStatus('正在保存到后端 R2…');
            const projectId = savedProjectId ?? (await api.createProject({
                title: lesson.title,
                description: lesson.intro,
                category: 'games',
                thumbnail: 'space',
                ...(sourceLessonId ? {sourceLessonId} : {})
            })).id;
            setSavedProjectId(projectId);
            localStorage.setItem(`cq_lesson_project_${lessonId}`, projectId);
            await api.uploadProjectFile(projectId, blob);
            setSaveStatus('已保存，刷新后也可以继续编辑');
        } catch (err) {
            setSaveStatus(err.message || '保存失败，请稍后再试');
        } finally {
            setSaving(false);
        }
    };

    const publishProject = async () => {
        if (!getToken()) {
            setSaveStatus('请先登录，再发布到作品广场');
            return;
        }
        if (!savedProjectId) {
            setSaveStatus('请先保存作品，再发布到作品广场');
            return;
        }
        setPublishing(true);
        try {
            await api.updateProject(savedProjectId, {
                isPublic: true,
                ...(sourceLessonId ? {sourceLessonId} : {})
            });
            setSaveStatus('已发布到作品广场');
        } catch (err) {
            setSaveStatus(err.message || '发布失败，请稍后再试');
        } finally {
            setPublishing(false);
        }
    };

    const requestEvaluation = () => {
        if (checking) return;
        setChecking(true);
        setEvaluation(null);
        setSaveStatus('正在检查作品…');
        setEvaluateRequestId(Date.now());
    };

    const handleProjectSummary = async projectSummary => {
        try {
            const data = await api.evaluateLesson(lessonId, {projectId: savedProjectId, projectSummary});
            setEvaluation(data.result);
            setSaveStatus(data.result.status === 'passed' ? '检查通过，做得很棒！' : '检查完成，请根据提示再调整一下');
        } catch (err) {
            setSaveStatus(err.message || '检查失败，请稍后再试');
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className={styles.wrap}>
            {/* 顶部工具条 */}
            <div className={styles.topbar}>
                <Button to="/quests" variant="ghost" size="sm" iconLeft={<Icon name="arrowRight" size={18} className={styles.flip} />}>
                    返回地图
                </Button>
                <div className={styles.lessonInfo}>
                    <span className={styles.lessonName}>{lesson.title}</span>
                    <span className={styles.lessonId}>
                        关卡 {lessonId} · {source === 'saved-project' ? '已恢复保存作品' : source === 'backend' ? '后端课程' : source === 'loading' ? '连接后端中…' : '离线备用'}
                    </span>
                </div>
                <button
                    type="button"
                    className={styles.guideToggle}
                    onClick={() => setGuideOpen(o => !o)}
                    aria-expanded={guideOpen}
                    aria-controls="lesson-guide"
                >
                    <Icon name="puzzle" size={18} />
                    {guideOpen ? '收起教程' : '展开教程'}
                </button>
                <button className={styles.saveBtn} type="button" onClick={requestSave} disabled={saving}>
                    {saving ? '保存中…' : '保存作品'}
                </button>
                <button className={styles.publishBtn} type="button" onClick={publishProject} disabled={publishing}>
                    {publishing ? '发布中…' : '发布到广场'}
                </button>
                <button className={styles.checkBtn} type="button" onClick={requestEvaluation} disabled={checking}>
                    {checking ? '检查中…' : '检查作品'}
                </button>
                {saveStatus && <span className={styles.saveStatus} aria-live="polite">{saveStatus}</span>}
            </div>

            <div className={[styles.body, guideOpen ? '' : styles.bodyFull].join(' ')}>
                {/* 左：教程步骤 */}
                {guideOpen && (
                    <aside id="lesson-guide" className={styles.guide}>
                        <div className={styles.guideHead}>
                            <div className={styles.guideMascot}><Mascot size={56} /></div>
                            <div>
                                <div className={styles.guideHi}>阿码的提示</div>
                                <p className={styles.guideIntro}>{lesson.intro}</p>
                            </div>
                        </div>

                        <div className={styles.progressRow}>
                            <div className={styles.progressTrack}>
                                <div className={styles.progressFill} style={{'--progress': progress / 100}} />
                            </div>
                            <span className={styles.progressPct}>{progress}%</span>
                        </div>

                        <ol className={styles.steps}>
                            {lesson.steps.map(step => {
                                const isDone = done.includes(step.n);
                                const isActive = activeStep?.n === step.n;
                                return (
                                    <li
                                        key={step.n}
                                        className={[
                                            styles.step,
                                            isDone ? styles.stepDone : '',
                                            isActive ? styles.stepActive : ''
                                        ].join(' ')}
                                        onClick={() => setActiveStep(step)}
                                    >
                                        <span className={styles.stepCheck}>
                                            {isDone ? <Icon name="check" size={16} /> : step.n}
                                        </span>
                                        <div>
                                            <div className={styles.stepTitle}>{step.title}</div>
                                            <div className={styles.stepDesc}>{step.desc}</div>
                                            {step.blockHint && <div className={styles.blockHint}>{step.blockHint}</div>}
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.doneBtn}
                                            onClick={e => {
                                                e.stopPropagation();
                                                toggle(step.n);
                                            }}
                                        >
                                            {isDone ? '重做' : '完成'}
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>

                        {progress === 100 && (
                            <div className={styles.doneBanner}>
                                <Icon name="medal" size={22} /> 太棒了，全部完成！
                            </div>
                        )}
                    </aside>
                )}

                {/* 右：真实 Scratch 编辑器 */}
                <section className={styles.editorArea}>
                    {evaluation && (
                        <div className={styles.evaluation} role="status">
                            <div className={styles.evaluationHead}>
                                <strong>{evaluation.status === 'passed' ? '已达成目标' : '还差一点'}</strong>
                                {evaluation.score != null && <span>{evaluation.score} 分</span>}
                                <button
                                    type="button"
                                    className={styles.evaluationClose}
                                    aria-label="关闭检查结果"
                                    onClick={() => setEvaluation(null)}
                                >
                                    ×
                                </button>
                            </div>
                            {evaluation.feedback?.length ? (
                                <ul>
                                    {evaluation.feedback.map(item => <li key={item}>{item}</li>)}
                                </ul>
                            ) : (
                                <p>所有检查项都通过了。</p>
                            )}
                        </div>
                    )}
                    <ScratchEditor
                        activeStep={activeStep}
                        lesson={lesson}
                        lessonId={lessonId}
                        locale="zh-cn"
                        saveRequestId={saveRequestId}
                        evaluateRequestId={evaluateRequestId}
                        onProjectData={handleProjectData}
                        onProjectSummary={handleProjectSummary}
                        onProjectError={message => setSaveStatus(message || '打包作品失败')}
                    />
                </section>
            </div>
        </div>
    );
}
